import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, Link, useSearchParams } from 'react-router-dom';
import { contactsAPI, organizationsAPI } from '../lib/api';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { useAuth } from '../context/AuthContext';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Checkbox } from '../components/ui/checkbox';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { toast } from 'sonner';
import { Users, ArrowLeft, Loader2, Save } from 'lucide-react';

const ROLE_TAGS = [
  { id: 'client', label: 'Client' },
  { id: 'venue', label: 'Venue' },
  { id: 'facilities', label: 'Facilities' },
  { id: 'vendor', label: 'Vendor' },
  { id: 'union', label: 'Union' },
  { id: 'technical', label: 'Technical' },
  { id: 'freelancer', label: 'Freelancer' },
  { id: 'AVF Staff', label: 'AVF Staff' },
];

const ContactForm = () => {
  const { id } = useParams();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { isViewer } = useAuth();
  const isEditing = Boolean(id);

  useEffect(() => {
    if (isViewer) { navigate('/contacts'); return; }
  }, [isViewer, navigate]);
  
  const [loading, setLoading] = useState(isEditing);
  const [saving, setSaving] = useState(false);
  const [organizations, setOrganizations] = useState([]);
  const [form, setForm] = useState({
    contact_name: '',
    organization_id: searchParams.get('organization_id') || '',
    title: '',
    email: '',
    phone: '',
    mobile: '',
    role_tags: [],
    notes: '',
  });

  useEffect(() => {
    loadOrganizations();
    if (isEditing) {
      loadContact();
    }
  }, [id]);

  const loadOrganizations = async () => {
    try {
      const response = await organizationsAPI.list({ limit: 1000 });
      // Handle paginated response format
      setOrganizations(response.data.data || response.data);
    } catch (error) {
      console.error('Failed to load organizations');
    }
  };

  const loadContact = async () => {
    try {
      const response = await contactsAPI.get(id);
      setForm({
        contact_name: response.data.contact_name || '',
        organization_id: response.data.organization_id || '',
        title: response.data.title || '',
        email: response.data.email || '',
        phone: response.data.phone || '',
        mobile: response.data.mobile || '',
        role_tags: response.data.role_tags || [],
        notes: response.data.notes || '',
      });
    } catch (error) {
      toast.error('Failed to load contact');
      navigate('/contacts');
    } finally {
      setLoading(false);
    }
  };

  const handleRoleToggle = (roleId) => {
    setForm((prev) => ({
      ...prev,
      role_tags: prev.role_tags.includes(roleId)
        ? prev.role_tags.filter((r) => r !== roleId)
        : [...prev.role_tags, roleId],
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!form.contact_name.trim()) {
      toast.error('Contact name is required');
      return;
    }

    setSaving(true);
    try {
      const data = {
        ...form,
        organization_id: form.organization_id === "none" || !form.organization_id ? null : form.organization_id,
      };

      if (isEditing) {
        await contactsAPI.update(id, data);
        toast.success('Contact updated');
      } else {
        const response = await contactsAPI.create(data);
        toast.success('Contact created');
        navigate(`/contacts/${response.data.id}`);
        return;
      }
      navigate(`/contacts/${id}`);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save contact');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-primary"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="contact-form">
      {/* Header */}
      <div className="flex items-center gap-4">
        <Button variant="ghost" size="icon" asChild>
          <Link to={isEditing ? `/contacts/${id}` : '/contacts'}>
            <ArrowLeft className="h-4 w-4" />
          </Link>
        </Button>
        <div>
          <h1 className="text-3xl sm:text-4xl font-bold tracking-tight flex items-center gap-3">
            <Users className="h-8 w-8 text-cyan-400" />
            {isEditing ? 'Edit Contact' : 'New Contact'}
          </h1>
        </div>
      </div>

      <form onSubmit={handleSubmit} className="space-y-6">
        <Card className="border-border/50 bg-card/50">
          <CardHeader>
            <CardTitle>Basic Information</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="contact_name">Contact Name *</Label>
                <Input
                  id="contact_name"
                  value={form.contact_name}
                  onChange={(e) => setForm({ ...form, contact_name: e.target.value })}
                  placeholder="Enter contact name"
                  className="bg-background/50"
                  data-testid="contact-name-input"
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="title">Job Title</Label>
                <Input
                  id="title"
                  value={form.title}
                  onChange={(e) => setForm({ ...form, title: e.target.value })}
                  placeholder="e.g., Event Coordinator"
                  className="bg-background/50"
                  data-testid="contact-title-input"
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="organization_id">Organization</Label>
              <Select
                value={form.organization_id || "none"}
                onValueChange={(value) => setForm({ ...form, organization_id: value === "none" ? "" : value })}
              >
                <SelectTrigger className="bg-background/50" data-testid="organization-select">
                  <SelectValue placeholder="Select organization (optional)" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">No organization</SelectItem>
                  {organizations.map((org) => (
                    <SelectItem key={org.id} value={org.id}>
                      {org.organization_name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label>Role Tags</Label>
              <div className="flex flex-wrap gap-4 mt-2">
                {ROLE_TAGS.map((role) => (
                  <div key={role.id} className="flex items-center space-x-2">
                    <Checkbox
                      id={role.id}
                      checked={form.role_tags.includes(role.id)}
                      onCheckedChange={() => handleRoleToggle(role.id)}
                      data-testid={`role-${role.id}`}
                    />
                    <Label htmlFor={role.id} className="cursor-pointer">
                      {role.label}
                    </Label>
                  </div>
                ))}
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="border-border/50 bg-card/50">
          <CardHeader>
            <CardTitle>Contact Details</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="email">Email</Label>
              <Input
                id="email"
                type="email"
                value={form.email}
                onChange={(e) => setForm({ ...form, email: e.target.value })}
                placeholder="email@example.com"
                className="bg-background/50"
                data-testid="contact-email-input"
              />
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="phone">Phone</Label>
                <Input
                  id="phone"
                  value={form.phone}
                  onChange={(e) => setForm({ ...form, phone: e.target.value })}
                  placeholder="(555) 123-4567"
                  className="bg-background/50"
                  data-testid="contact-phone-input"
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="mobile">Mobile</Label>
                <Input
                  id="mobile"
                  value={form.mobile}
                  onChange={(e) => setForm({ ...form, mobile: e.target.value })}
                  placeholder="(555) 987-6543"
                  className="bg-background/50"
                  data-testid="contact-mobile-input"
                />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="border-border/50 bg-card/50">
          <CardHeader>
            <CardTitle>Notes</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-2">
              <Label htmlFor="notes">General Notes</Label>
              <Textarea
                id="notes"
                value={form.notes}
                onChange={(e) => setForm({ ...form, notes: e.target.value })}
                placeholder="Any additional notes about this contact..."
                className="bg-background/50 min-h-[120px]"
                data-testid="contact-notes-input"
              />
            </div>
          </CardContent>
        </Card>

        <div className="flex justify-end gap-4">
          <Button
            type="button"
            variant="outline"
            asChild
          >
            <Link to={isEditing ? `/contacts/${id}` : '/contacts'}>
              Cancel
            </Link>
          </Button>
          <Button type="submit" disabled={saving} data-testid="save-contact-btn">
            {saving ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                Saving...
              </>
            ) : (
              <>
                <Save className="mr-2 h-4 w-4" />
                {isEditing ? 'Update' : 'Create'} Contact
              </>
            )}
          </Button>
        </div>
      </form>
    </div>
  );
};

export default ContactForm;
